import { useEffect, useState } from "react";
import {
    useBeforeUnload,
    useLocation,
    useNavigate
} from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
    sendPhoneNumber,
    sendOTP,
} from "../../services/otp/otp.service";
import { issueCoupon } from "../../services/ads/ads.service";
import { links } from "../../constants/links";

import { Loading } from "../../components";
import { Typography } from "@mui/material";
import SVG from "react-inlinesvg";
import tickIcon from "../../assets/icons/Eo_circle_green_checkmark.svg";
import tickIcon2 from "../../assets/icons/Eo_circle_green_checkmark_2.svg";
import barsRotateFade from "../../assets/icons/bars-rotate-fade.svg";
import { ReportErrorLink } from "../../components/ReportErrorLink";
import LogoHeader from "../../components/LogoHeader";


const getInitialOTP = () => [1, 2, 3, 4, 5, 6].map(() => "");

const OTPInsertOTP = () => {
    const navigate = useNavigate();

    if (localStorage.getItem("otp-page-expired") === "1") {
        navigate(links.OTP_PAGE_EXPIRED);
    }

    const { t } = useTranslation();

    const location = useLocation();

    const defaultTimeout: number = location.state?.OTPExpiredTimeInSeconds ?? 60;

    const [secondsRemain, setSecondsRemain] = useState(defaultTimeout);

    const [inProgress, setInProgress] = useState(false);

    const [verified, setVerified] = useState(false);

    const [otp, setOTP] = useState(getInitialOTP);

    const [isOTPInvalid, setIsOTPInvalid] = useState(false);

    const { engageSession } = location.state || {};

    useEffect(
        () => {
            const intervalId = setInterval(
                () => {
                    setSecondsRemain(s => s > 0 ? s - 1 : 0);
                },
                1000
            );

            return () => {
                clearInterval(intervalId);
            };
        },
        []
    );

    useEffect(
        () => {
            history.pushState(null, "", null);

            const callback = (event: any) => {
                history.pushState(null, "", null);
            };
            window.addEventListener("popstate", callback);

            return () => {
                window.removeEventListener("popstate", callback);
            };
        },
        []
    );

    useBeforeUnload(
        () => {
            localStorage.setItem("otp-page-expired", "1");
        }
    );

    const phoneNumber = location.state?.phoneNumber;
    const countryCode = location.state?.countryCode;
    const countryPhonePrefix = location.state?.countryPhonePrefix;

    const minutesRemain = Math.floor(secondsRemain / 60);

    const resendOnClickHandler = () => {
        setInProgress(true);
        setIsOTPInvalid(false);
        sendPhoneNumber(countryCode, countryPhonePrefix, phoneNumber)
            .then(
                res => {
                    if (res.status === 200) {
                        setSecondsRemain(defaultTimeout);
                    }

                    setInProgress(false);
                }
            )
            .catch(
                error => {
                    if (error.response.data?.error_code === "maximum_attempts_reached") {
                        navigate(links.OTP_MAX_ATTEMPT_REACHED);
                    }
                }
            );
    };

    const onKeyDown = (event: any) => {
        if (event.key === "Backspace") {
            const index = parseInt(event.target.dataset.id);

            if (otp[index] === "") {
                const nextFocusTarget = event.target.parentElement.querySelectorAll("input")[index - 1];

                if (nextFocusTarget instanceof HTMLInputElement) {
                    nextFocusTarget.focus();
                }
            } else {
                // This is a hack to delete the current value in the input box when the cursor is place before that value in the input box when user press delete/backspace key
                event.target.select();
            }
        }
    };

    const onChange = (event: any) => {
        event.preventDefault();

        let value = event.target.value;

        const values = [];

        const newOTP = otp.slice();

        const index = parseInt(event.target.dataset.id);

        if (/^[0-9][0-9]+$/.test(value)) {
            // if there is already a value in the current input, remove it from the otp first
            // as it will be part of the value
            if (newOTP[index] !== "") {
                newOTP[index] = "";
            }

            for (let i = 0; i < value.length; i++) {
                values.push(value[i]);
            }
        } else if (value.length === 1) {
            // if there is already a value in the current input, remove it from the otp first
            // as it will be part of the value
            if (newOTP[index] !== "") {
                newOTP[index] = "";
            }

            values.push(value);
        }

        if (values.length > 0) {
            let i = 0;
            while (i < 6) {
                if (newOTP[i] == "") {
                    for (let j = 0; j < values.length && i < 6; j++) {
                        newOTP[i] = values[j];

                        i++;
                    }

                    setOTP(newOTP);
                    let k = i > 5 ? 5 : i;
                    const nextFocusTarget = event.target.parentElement.querySelectorAll("input")[k];
                    if (nextFocusTarget instanceof HTMLInputElement) {
                        nextFocusTarget.focus();
                    }

                    // End the loop
                    i = 6;
                }

                i++;
            }
        } else {
            newOTP[index] = "";

            setOTP(newOTP);
        }
    };

    const onSubmitHandler = () => {
        setIsOTPInvalid(false);

        const otpString = otp.join("");

        setInProgress(true);

        sendOTP(otpString, phoneNumber)
            .then(
                res => {
                    setInProgress(false);

                    if (res.status === 200) {
                        setVerified(true);

                        issueCoupon({ engage_session: engageSession }).then((res) => {
                            console.log("issueCoupon", res);
                            setTimeout(
                                () => {
                                    const state = {};

                                    Object.keys(location.state).forEach(
                                        key => {
                                            if (!["otp_use_only__subcampaign_id", "phoneNumber"].includes(key)) {
                                                Object.defineProperty(state, key, { value: location.state[key] });
                                            }
                                        }
                                    );
                                    if (engageSession) {
                                        navigate(`${links.USE_REWARD_V2}/?engage-session=${engageSession}`,
                                        { state: { isFromOtpPage: true }
                                    });
                                    } else {
                                        navigate(links.USE_REWARD.replace(":id", location.state?.otp_use_only__subcampaign_id), { state });
                                    }
                                },
                                2000
                            );

                        }).catch((error) => {
                            const errorMsg = error?.response?.data?.error_msg;
                            if (errorMsg === "coupon_error") {
                                navigate(`${links.SMART_RETAIL_ERROR}?error-msg=coupon_error`);
                            } else {
                                navigate(`${links.SMART_RETAIL_ERROR}?error-msg=coupon_failed`);
                            }
                        });
                    }
                }
            )
            .catch(
                error => {
                    const errorCode = error.response.data?.error_code;

                    if (errorCode === "invalid_otp") {
                        setSecondsRemain(0); // show resend button
                        setOTP(getInitialOTP);
                        setIsOTPInvalid(true);
                        setInProgress(false);
                    } else if (errorCode === "maximum_attempts_reached" || error.response.data.resendable === false) {
                        navigate(links.OTP_MAX_ATTEMPT_REACHED);
                    }
                }
            );
    };

    const disabled = otp.some(s => s === "");

    return inProgress
        ? <Loading />
        : (
            <div style={{ height: "100%", maxHeight: "100%", padding: "14px 20px", position: "relative" }}>
                    <LogoHeader enableLanguage={true}/>
                {
                    verified
                        ? (
                            <>
                                <Typography
                                    style={{
                                        color: "#5888C4",
                                        fontWeight: 700,
                                        fontSize: 24,
                                        marginTop: "50%",
                                        textAlign: "center"
                                    }}
                                >
                                    {t("accountVerified")}
                                    {
                                        t("accountVerified2") !== "accountVerified2"
                                            ? (
                                                <>
                                                    <br />
                                                    {t("accountVerified2")}
                                                </>
                                            )
                                            : null
                                    }
                                </Typography>
                                {
                                    verified
                                        ? (
                                            <SVG
                                                src={tickIcon2}
                                                style={{
                                                    width: 48,
                                                    display: "block",
                                                    margin: "32px auto 146px",
                                                }}
                                            />
                                        )
                                        : null
                                }
                            </>
                        )
                        : (
                            <>
                                <div style={{ backgroundColor: "#E8F8EB", borderRadius: 16, padding: 20, display: "flex", alignItems: "flex-start", columnGap: 10, marginTop: "50%" }}>
                                    <SVG src={tickIcon} style={{ width: 24, marginTop: 4 }} />
                                    {t("weHaveJustSentAMessageToYourSMS")} +{countryPhonePrefix}{phoneNumber}.
                                </div>
                                <div style={{ textAlign: "center", marginTop: 8 }}>
                                    {
                                        isOTPInvalid
                                            ? <><span style={{ color: "red" }}>{t("incorrectVerificationCode")}</span> <button onClick={resendOnClickHandler} className="otp-btn">{t("resend")}</button></>
                                            : secondsRemain > 0
                                                ? <>{t("verificationCodeValidFor")} <span style={{ color: "red" }}>{minutesRemain > 0 ? `${minutesRemain} ${t("minutes")} ` : ""}{secondsRemain % 60} {t("seconds")}</span></>
                                                : <>{t("dontReceiveAnyMessage")} <button onClick={resendOnClickHandler} className="otp-btn">{t("resend")}</button></>
                                    }
                                </div>
                                <div style={{ display: "flex", marginTop: 80, maxWidth: "100%", flexWrap: "nowrap", columnGap: 8 }}>
                                    {
                                        otp.map(
                                            (_, index) => (
                                                <input
                                                    key={index}
                                                    data-id={index}
                                                    type="number"
                                                    inputMode="numeric"
                                                    pattern="^[0-9]$"
                                                    min={1}
                                                    max={9}
                                                    style={
                                                        {
                                                            outline: 0,
                                                            borderRadius: 0, // remove rounded border in iOS
                                                            WebkitBorderRadius: 0, // remove rounded border in iOS
                                                            borderTop: "unset",
                                                            borderLeft: "unset",
                                                            borderRight: "unset",
                                                            borderBottom: "2px solid #000",
                                                            textAlign: "center",
                                                            fontSize: 24,
                                                            fontWeight: 700,
                                                            width: "calc((100% - (5 * 8px)) / 6)"
                                                        }
                                                    }
                                                    value={otp[index] ?? ""}
                                                    onKeyDown={onKeyDown}
                                                    onChange={onChange}
                                                    maxLength={1}
                                                />
                                            )
                                        )
                                    }
                                </div>
                            </>
                        )
                }
                <button
                    className="otp-btn"
                    disabled={disabled && !verified}
                    onClick={verified ? () => {} : onSubmitHandler}
                    style={{
                        fontWeight: 700,
                        borderRadius: 16,
                        padding: 15,
                        marginTop: 32,
                        width: "100%",
                    }}
                >
                    {verified ? t("redirecting") + " ..." : t("submit")}
                </button>
                {
                    verified
                        ? (
                            <SVG
                                src={barsRotateFade}
                                style={{
                                    display: "block",
                                    margin: "32px auto 0",
                                }}
                            />
                        )
                        : null
                }
                {
                    verified
                        ? null
                        : (
                            <div style={{ marginTop: 24, display: "flex", justifyContent: "center" }}>
                                <ReportErrorLink />
                            </div>
                        )
                }
            </div>
        );
};

export default OTPInsertOTP;
